import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Autocomplete, Checkbox, CircularProgress, TextField, Typography } from '@mui/material';
import RssFeedIcon from '@mui/icons-material/RssFeed';

import DeviceStore from '../../../stores/dev-store';
import { DeviceInfo, deviceName, DeviceType } from '../../../types/device';

export interface DeviceFilterProps {
    nodeId: number;
    selectedDeviceIds?: number[];
    onChange: (deviceIds: number[]) => void;
}

function deviceLabel(device: DeviceInfo): string {
    const type = (device.info?.deviceType as DeviceType) ?? DeviceType.Device_Unknown;
    return `${deviceName(type)}-${device.device_id}`;
}

const DeviceFilter = observer(({ nodeId, selectedDeviceIds, onChange }: DeviceFilterProps) => {
    const [store] = useState(() => new DeviceStore(nodeId));
    const [selectedItems, setSelectedItems] = useState<DeviceInfo[]>([]);
    const initRef = useRef(selectedDeviceIds ?? []);

    useEffect(() => {
        store
            .load()
            .then(() => {
                if (initRef.current.length > 0) {
                    setSelectedItems(store.items.filter((d) => initRef.current.includes(d.device_id)));
                }
            })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (_: React.SyntheticEvent, newValue: DeviceInfo[]) => {
        setSelectedItems(newValue);
        onChange(newValue.map((d) => d.device_id));
    };

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={store.items}
            value={selectedItems}
            onChange={handleChange}
            loading={store.loading}
            getOptionLabel={deviceLabel}
            isOptionEqualToValue={(opt, val) => opt.device_id === val.device_id}
            noOptionsText="Нет устройств"
            size="small"
            sx={{ mx: 1, mb: 1 }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={selectedItems.length === 0 ? 'Все' : undefined}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {store.loading && <CircularProgress size={14} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
            renderOption={(props, option, { selected }) => (
                <li {...props} key={option.device_id}>
                    <Checkbox size="small" checked={selected} disableRipple sx={{ p: 0, mr: 1 }} />
                    <RssFeedIcon sx={{ fontSize: 14, mr: 0.5, flexShrink: 0, color: 'text.secondary' }} />
                    <Typography variant="body2" noWrap>
                        {deviceLabel(option)}
                    </Typography>
                </li>
            )}
        />
    );
});

export default DeviceFilter;
